import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { environment } from 'src/environments/environment';

@Injectable({ providedIn: 'root' })
export class CookieHubService {
  private scriptLoaded = false;
  private gtmScript?: HTMLScriptElement;
  private gtmIframe?: HTMLIFrameElement;
  private hotjarLoaded = false;

  private readonly gtmId = 'GTM-TWKMH32';
  private readonly gaMeasurementId = 'G-BT4M4E44YQ';
  private readonly hotjarId = 2539814;
  private readonly hotjarVersion = 7;
  private gaScriptLoaded = false;

  private consentDefaultsSet = false;

  private readonly analyticsCookies = {
    names: ['_ga', '_gid', 'hjactiveviewportids', 'hjviewportid'],
    prefixes: ['_gat', '_ga_', 'mp_', '_hjsessionuser_', '_hjsession_', '__mpq']
  };

  private readonly marketingCookies = {
    names: ['_gcl_au', '_gcl_ls', '_fbp', 'lastexternalreferrer', 'lastexternalreferrertime', 'topicslastreferencetime', 'ide'],
    prefixes: []
  };

  constructor(@Inject(PLATFORM_ID) private platformId: any) {}

  load(lang: string) {
    if (!isPlatformBrowser(this.platformId)) return;
    if (this.scriptLoaded) return;

    this.scriptLoaded = true;

    const script = document.createElement('script');
    script.src = environment.production ? 'https://cdn.cookiehub.eu/c2/ea03a68b.js' : 'https://dash.cookiehub.com/dev/ea03a68b.js';
    script.async = true;
    script.fetchPriority = 'high';
    script.onload = () => this.initializeCookieHub(lang);

    document.head.appendChild(script);
  }

  private initializeCookieHub(lang: string) {
    const cookiehub = (window as any).cookiehub;
    if (!cookiehub) return;

    cookiehub.load({
      language: lang,
      onStatusChange: () => this.handleConsentChange(),
    });

    this.handleConsentChange();
  }

  private handleConsentChange() {
    const analyticsAllowed = this.hasConsent('analytics');
    const marketingAllowed = this.hasConsent('marketing');

    this.ensureGtag();
    this.pushConsentDefaults();
    this.setConsentMode(analyticsAllowed, marketingAllowed);
    this.setGaDisableFlag(analyticsAllowed);

    if (analyticsAllowed) {
      this.injectGA();
      this.injectHotjar();
    } else {
      this.removeGA();
      this.clearCategory(this.analyticsCookies);
    }

    if (marketingAllowed) {
      this.injectGtm();
    } else {
      this.removeGtm();
      this.clearCategory(this.marketingCookies);
    }
  }

  private hasConsent(category: 'analytics' | 'marketing'): boolean {
    const cookiehub = (window as any).cookiehub;
    return !!cookiehub?.hasConsented && cookiehub.hasConsented(category);
  }

  private injectGA() {
    if (this.gaScriptLoaded) return;

    // Load GA4 script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${this.gaMeasurementId}`;
    document.head.appendChild(script);

    // Initialize GA4 config AFTER script load
    script.onload = () => {
      (window as any).gtag('js', new Date());
      (window as any).gtag('config', this.gaMeasurementId, { send_page_view: true });
    };

    this.gaScriptLoaded = true;
  }

  private removeGA() {
    // Disable GA
    (window as any)[`ga-disable-${this.gaMeasurementId}`] = true;

    // Remove script tag
    const scripts = document.querySelectorAll(
      `script[src*="gtag/js?id=${this.gaMeasurementId}"]`
    );
    scripts.forEach(s => s.parentNode?.removeChild(s));

    this.gaScriptLoaded = false;
  }

  private injectHotjar() {
    if (this.hotjarLoaded) return;

    const w = window as any;
    w.hj =
      w.hj ||
      function () {
        (w.hj.q = w.hj.q || []).push(arguments);
      };
    w._hjSettings = { hjid: this.hotjarId, hjsv: this.hotjarVersion };

    const head = document.getElementsByTagName('head')[0];
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://static.hotjar.com/c/hotjar-${this.hotjarId}.js?sv=${this.hotjarVersion}`;
    head.appendChild(script);

    this.hotjarLoaded = true;
  }

  private injectGtm() {
    if (this.gtmScript) return;

    (window as any).dataLayer = (window as any).dataLayer || [];
    (window as any).dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });

    const script = document.createElement('script');
    script.id = 'cookiehub-gtm';
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${this.gtmId}`;
    document.head.appendChild(script);
    this.gtmScript = script;

    const iframe = document.createElement('iframe');
    iframe.src = `https://www.googletagmanager.com/ns.html?id=${this.gtmId}`;
    iframe.height = '0';
    iframe.width = '0';
    iframe.style.display = 'none';
    iframe.style.visibility = 'hidden';
    document.body.appendChild(iframe);
    this.gtmIframe = iframe;
  }

  private removeGtm() {
    if (this.gtmScript?.parentNode) this.gtmScript.parentNode.removeChild(this.gtmScript);
    if (this.gtmIframe?.parentNode) this.gtmIframe.parentNode.removeChild(this.gtmIframe);

    this.gtmScript = undefined;
    this.gtmIframe = undefined;
  }

  private ensureGtag() {
    const w = window as any;
    w.dataLayer = w.dataLayer || [];
    if (typeof w.gtag !== 'function') {
      w.gtag = function () {
        w.dataLayer.push(arguments);
      };
    }
  }

  private pushConsentDefaults() {
    if (this.consentDefaultsSet) return;

    (window as any).gtag('consent', 'default', {
      analytics_storage: 'denied',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
    });

    this.consentDefaultsSet = true;
  }

  private setConsentMode(analyticsAllowed: boolean, marketingAllowed: boolean) {
    (window as any).gtag('consent', 'update', {
      analytics_storage: analyticsAllowed ? 'granted' : 'denied',
      ad_storage: marketingAllowed ? 'granted' : 'denied',
      ad_user_data: marketingAllowed ? 'granted' : 'denied',
      ad_personalization: marketingAllowed ? 'granted' : 'denied',
    });
  }

  private setGaDisableFlag(analyticsAllowed: boolean) {
    (window as any)[`ga-disable-${this.gaMeasurementId}`] = !analyticsAllowed;
  }

  private clearCategory(list: { names: string[]; prefixes: string[] }) {
    const cookies = document.cookie.split(';');

    cookies.forEach(raw => {
      const [name] = raw.split('=');
      const trimmed = name?.trim();
      if (!trimmed) return;

      if (this.shouldDelete(trimmed, list)) {
        this.expireCookie(trimmed);
      }
    });
  }

  private shouldDelete(name: string, list: { names: string[]; prefixes: string[] }) {
    const lower = name.toLowerCase();
    return (
      list.names.some(target => lower === target.toLowerCase()) ||
      list.prefixes.some(prefix => lower.startsWith(prefix.toLowerCase()))
    );
  }

  private expireCookie(name: string) {
    const domains = this.getPossibleDomains();
    domains.forEach(domain => {
      document.cookie =
        `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/;` +
        (domain ? ` domain=${domain};` : '');
    });
  }

  private getPossibleDomains(): (string | undefined)[] {
    const host = window.location.hostname;
    const parts = host.split('.');

    const domains = new Set<string | undefined>();
    domains.add(undefined);
    domains.add(host);

    if (parts.length > 1) domains.add(`.${parts.slice(-2).join('.')}`);
    if (parts.length > 2) domains.add(`.${host}`);

    return Array.from(domains);
  }
}
